import React, { useState } from 'react';
import { Play, ArrowRight } from 'lucide-react';

const videos = [
  {
    id: 1,
    src: 'https://www.youtube.com/embed/2bw_YbYe294',
    thumbnail: 'https://img.youtube.com/vi/2bw_YbYe294/hqdefault.jpg',
    title: 'Obesity Treatment at GastroCare Bhopal',
    description:
      'Expert insights on obesity treatment and weight management solutions offered at GastroCare Bhopal.',
  },
  {
    id: 2,
    src: 'https://www.youtube.com/embed/FLIi2MQJg20',
    thumbnail: 'https://img.youtube.com/vi/FLIi2MQJg20/hqdefault.jpg',
    title: 'Bariatric Surgery & Metabolic Health',
    description:
      'A comprehensive overview of bariatric procedures and their impact on metabolic health.',
  },
  {
    id: 3,
    src: 'https://www.youtube.com/embed/bqUJe2Tm_wk',
    thumbnail: 'https://img.youtube.com/vi/bqUJe2Tm_wk/hqdefault.jpg',
    title: 'Patient Journey – Weight Loss Success',
    description:
      'Hear directly from our patients about their transformative journey at GastroCare Bhopal.',
  },
  {
    id: 4,
    src: 'https://www.youtube.com/embed/1GtRI53lnz4',
    thumbnail: 'https://img.youtube.com/vi/1GtRI53lnz4/hqdefault.jpg',
    title: 'Endoscopic Obesity Treatment Without Surgery',
    description:
      'Learn about endoscopic procedures for treating obesity safely without traditional surgery.',
  },
  {
    id: 5,
    src: 'https://www.youtube.com/embed/feUOuvTqjys',
    thumbnail: 'https://img.youtube.com/vi/feUOuvTqjys/hqdefault.jpg',
    title: 'GastroCare Bhopal Success Story',
    description:
      'Hear about the excellent care and treatments provided by Dr. Sanjay Kumar and team.',
  },
];

/* ─── Video Modal ──────────────────────────────────────────────── */
const VideoModal = ({ video, onClose }) => {
  if (!video) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-[10000] w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors text-xl leading-none"
        aria-label="Close"
      >
        ✕
      </button>
      <div
        className="relative w-full max-w-4xl rounded-2xl overflow-hidden shadow-2xl bg-black"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aspect-video w-full">
          <iframe
            src={`${video.src}?autoplay=1&rel=0`}
            title={video.title}
            className="w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="p-5 bg-slate-900">
          <h3 className="text-white font-bold text-base">{video.title}</h3>
          <p className="text-slate-400 text-sm mt-1">{video.description}</p>
        </div>
      </div>
    </div>
  );
};

/* ─── Video Thumbnail Card ─────────────────────────────────────── */
const VideoCard = ({ video, onPlay }) => (
  <div
    className="group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border border-slate-100"
    onClick={() => onPlay(video)}
  >
    {/* Thumbnail */}
    <div className="relative aspect-video overflow-hidden bg-slate-200">
      <img
        src={video.thumbnail}
        alt={video.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      {/* Subtle dark overlay */}
      <div className="absolute inset-0 bg-black/15 group-hover:bg-black/25 transition-colors duration-300" />

      {/* Play button – white circle with maroon play icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
          <Play className="w-7 h-7 text-[#800000] ml-1" fill="#800000" />
        </div>
      </div>
    </div>
  </div>
);

/* ─── Main Section ─────────────────────────────────────────────── */
const VideoSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section
      id="videos"
      className="py-16 md:py-20 bg-slate-50"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');`}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">
            Videos of Dr. Sanjay Kumar
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 p-2">
            Educational Content
          </h2>
          {/* <p className="text-slate-500 text-base mt-1">
            Explanatory videos of Dr. Sanjay Kumar
          </p> */}
        </div>

        {/* Video thumbnail flex layout for centered last row */}
        <div className="flex flex-wrap justify-center gap-5 mb-8">
          {videos.map((video) => (
            <div
              key={video.id}
              className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.33%-14px)]"
            >
              <VideoCard video={video} onPlay={setActiveVideo} />
            </div>
          ))}
        </div>

        {/* VIEW MORE VIDEOS – pill button matching screenshot */}
        {/* <div className="text-center">
          <a
            href="https://www.youtube.com/@DrSanjayKumarGastrocare"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#800000] hover:bg-[#6b0000] text-white font-bold uppercase tracking-widest text-sm px-6 py-3 rounded-full transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 group"
          >
            View More Videos
            <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors duration-200">
              <ArrowRight className="w-4 h-4" />
            </span>
          </a>
        </div> */}
      </div>

      {/* Lightbox Modal */}
      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    </section>
  );
};

export default VideoSection;
